/**
 * Checkout Store
 *
 * Manages shopping cart and checkout flow state.
 * Uses existing payment types and paymentApi for processing.
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import type { Cart, CartItem, Transaction } from '../types'
import { paymentApi } from '../api'

type CheckoutStep = 'cart' | 'payment' | 'confirmation'

interface CheckoutState {
  // Cart
  cart: Cart
  isCartOpen: boolean

  // Checkout flow
  checkoutStep: CheckoutStep
  currentOrder: Transaction | null
  isProcessing: boolean

  // Order history
  orders: Transaction[]
  isLoading: boolean

  // Error
  error: string | null

  // Cart actions
  addToCart: (item: Omit<CartItem, 'id'> & { id?: string }) => void
  removeFromCart: (itemId: string) => void
  updateQuantity: (itemId: string, quantity: number) => void
  clearCart: () => void
  isInCart: (referenceId: string) => boolean

  // Coupon
  applyCoupon: (code: string) => Promise<boolean>
  removeCoupon: () => void

  // Checkout flow
  setStep: (step: CheckoutStep) => void
  processCheckout: (paymentMethodId: string) => Promise<boolean>

  // Order history
  fetchOrders: () => Promise<void>

  // Cart drawer
  openCart: () => void
  closeCart: () => void

  // Utility
  clearError: () => void
  reset: () => void
}

const emptyCart: Cart = {
  items: [],
  subtotal: 0,
  discount: 0,
  tax: 0,
  total: 0,
}

function recalculate(items: CartItem[], discount: number, couponCode?: string): Cart {
  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0)
  const tax = Math.round(subtotal * 0.08 * 100) / 100 // 8% tax estimate
  const total = Math.max(0, subtotal - discount + tax)
  return { items, subtotal, discount, tax, total, couponCode }
}

export const useCheckoutStore = createWithEqualityFn<CheckoutState>((set, get) => ({
  cart: { ...emptyCart },
  isCartOpen: false,
  checkoutStep: 'cart',
  currentOrder: null,
  isProcessing: false,
  orders: [],
  isLoading: false,
  error: null,

  addToCart: (item) => {
    const { cart } = get()
    const id = item.id || `cart-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`

    // Check if item with same referenceId already exists
    const existing = cart.items.find((i) => i.referenceId === item.referenceId)
    if (existing) {
      // Increment quantity
      const items = cart.items.map((i) =>
        i.referenceId === item.referenceId ? { ...i, quantity: i.quantity + (item.quantity || 1) } : i
      )
      set({ cart: recalculate(items, cart.discount, cart.couponCode) })
      return
    }

    const newItem: CartItem = { ...item, id, quantity: item.quantity || 1 }
    const items = [...cart.items, newItem]
    set({ cart: recalculate(items, cart.discount, cart.couponCode) })
  },

  removeFromCart: (itemId) => {
    const { cart } = get()
    const items = cart.items.filter((i) => i.id !== itemId)
    set({ cart: recalculate(items, items.length > 0 ? cart.discount : 0, items.length > 0 ? cart.couponCode : undefined) })
  },

  updateQuantity: (itemId, quantity) => {
    const { cart } = get()
    if (quantity <= 0) {
      get().removeFromCart(itemId)
      return
    }
    const items = cart.items.map((i) => (i.id === itemId ? { ...i, quantity } : i))
    set({ cart: recalculate(items, cart.discount, cart.couponCode) })
  },

  clearCart: () => {
    set({ cart: { ...emptyCart }, checkoutStep: 'cart', currentOrder: null })
  },

  isInCart: (referenceId) => {
    return get().cart.items.some((i) => i.referenceId === referenceId)
  },

  applyCoupon: async (code) => {
    const { cart } = get()
    set({ error: null })
    try {
      const res = await paymentApi.applyCoupon(cart, code)
      if (res.success) {
        set({ cart: res.data })
        return true
      }
      set({ error: res.error || 'Invalid coupon code' })
      return false
    } catch {
      set({ error: 'Failed to apply coupon' })
      return false
    }
  },

  removeCoupon: () => {
    const { cart } = get()
    set({ cart: recalculate(cart.items, 0) })
  },

  setStep: (step) => set({ checkoutStep: step }),

  processCheckout: async (paymentMethodId) => {
    const { cart } = get()
    set({ isProcessing: true, error: null })

    try {
      // Create checkout session
      const sessionRes = await paymentApi.createCheckoutSession(cart)
      if (!sessionRes.success) {
        throw new Error(sessionRes.error || 'Failed to create checkout session')
      }

      // Process payment
      const paymentRes = await paymentApi.processPayment(sessionRes.data.id, paymentMethodId)
      if (!paymentRes.success) {
        throw new Error(paymentRes.error || 'Payment failed')
      }

      set({
        isProcessing: false,
        currentOrder: paymentRes.data,
        checkoutStep: 'confirmation',
      })
      return true
    } catch (err) {
      set({
        isProcessing: false,
        error: err instanceof Error ? err.message : 'Payment failed',
      })
      return false
    }
  },

  fetchOrders: async () => {
    set({ isLoading: true, error: null })
    try {
      const res = await paymentApi.getTransactions()
      if (res.success) {
        set({ orders: res.data, isLoading: false })
      } else {
        set({ isLoading: false, error: res.error || 'Failed to load orders' })
      }
    } catch {
      set({ isLoading: false, error: 'Failed to load orders' })
    }
  },

  openCart: () => set({ isCartOpen: true }),
  closeCart: () => set({ isCartOpen: false }),
  clearError: () => set({ error: null }),

  reset: () =>
    set({
      cart: { ...emptyCart },
      isCartOpen: false,
      checkoutStep: 'cart',
      currentOrder: null,
      isProcessing: false,
      orders: [],
      isLoading: false,
      error: null,
    }),
}))

// Selectors
export const selectCartItemCount = (state: CheckoutState) =>
  state.cart.items.reduce((sum, item) => sum + item.quantity, 0)

export const selectCartTotal = (state: CheckoutState) => state.cart.total
